import { ColumnDef, SortingState } from "@tanstack/react-table";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { IPatient, PatientListTableContent } from "../../../../@types/Patient";
import { TableSkeleton } from "../../../../components/TableSkeleton";
import { RowFlagsFn, TableSorted } from "../../../../components/TableSorted";
import {
  PATIENT_LIST_LAST_SELECTED_ID,
  PATIENT_LIST_LAST_SELECTED_PAGE,
  PATIENT_LIST_SCROLL_POSITION,
  PATIENT_LIST_SORTING_STATE,
} from "../../../../constants/localStorageKeys";
import { patientListVariants } from "../../../../constants/patientListVariants";
import useIsMobile from "../../../../hooks/useIsMobile";
import { MobileList } from "./components/MobileList";

type PatientListTableProps = {
  content: PatientListTableContent[];
  isLoading?: boolean;
  variant: string;
  columns: ColumnDef<any, string>[];
  isAdherenceList?: boolean;
  getRowFlags?: RowFlagsFn<any>;
  highlightRowId?: number | null;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  manualSorting?: boolean;
  /** Current page number — saved to sessionStorage on row click so going
   *  back from the patient page restores the same page. */
  currentPage?: number;
};

function PatientListSortedTableInner({
  content,
  variant,
  isLoading = false,
  isAdherenceList,
  columns,
  getRowFlags,
  highlightRowId,
  sorting,
  onSortingChange,
  manualSorting,
  currentPage,
}: PatientListTableProps) {
  const { isMobile } = useIsMobile();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleRowClick = (patientContent: IPatient) => {
    sessionStorage.setItem(
      PATIENT_LIST_LAST_SELECTED_ID,
      String(patientContent.id)
    );
    if (currentPage !== undefined) {
      sessionStorage.setItem(
        PATIENT_LIST_LAST_SELECTED_PAGE,
        String(currentPage)
      );
    }

    navigate(
      `/patients/${patientContent.id}/${
        variant === patientListVariants.ALERTS ? "alerts" : "overview"
      }`
    );
  };

  const emptyStateText = t(`patientList_empty_${variant}`);

  if (isLoading) {
    return <TableSkeleton columnsNumber={8} rowsNumber={14} />;
  }

  if (isAdherenceList) {
    const handleAdherenceRowClick = (patientContent: IPatient) => {
      navigate(`/patients/${patientContent.id}/overview`);
    };

    return (
      <TableSorted
        isAdherence
        data={content}
        onRowClick={handleAdherenceRowClick}
        emptyStateText={t("patientList_emptyDefault")}
        columns={columns}
        getRowFlags={getRowFlags}
      />
    );
  }

  if (isMobile) {
    return (
      <MobileList
        onRowClick={handleRowClick}
        emptyStateText={emptyStateText}
        data={content}
      />
    );
  }

  return (
    <TableSorted
      data={content}
      onRowClick={handleRowClick}
      emptyStateText={emptyStateText}
      columns={columns}
      scrollPositionKey={PATIENT_LIST_SCROLL_POSITION}
      sortingStateKey={PATIENT_LIST_SORTING_STATE}
      highlightRowId={highlightRowId}
      sorting={sorting}
      onSortingChange={onSortingChange}
      manualSorting={manualSorting}
    />
  );
}

// Memoised so unrelated re-renders of the parent (modal open/close, toast,
// hover state, etc.) don't force the table — which has a virtualised body
// and 1000+ rows of cell JSX — to re-render. Only re-renders when the
// listed props change reference.
export const PatientListSortedTable = memo(PatientListSortedTableInner);
